import { useCallback } from 'react';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { INBOX_TYPES, type Inbox, type InboxItem, type SocialNotification } from '@teaplus/shared';
import { api } from './api';
import { looked } from './friend-looks';
import { useCanSocialise, useLooks } from './social';

const inboxKey = ['inbox'] as const;
const kept = new Set<string>(INBOX_TYPES);

/** The bell's notifications, newest first, with friends' nicknames and pictures. */
export function useInbox() {
  const can = useCanSocialise();
  const v = useLooks();
  const select = useCallback(
    (d: Inbox) => ({ ...d, items: d.items.map((i) => ({ ...i, from: looked(i.from) })) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v],
  );
  return useQuery({
    queryKey: inboxKey,
    queryFn: () => api<Inbox>('/api/notifications'),
    select,
    enabled: can,
    refetchInterval: 120_000,
  });
}

export const refreshInbox = (qc: QueryClient) => void qc.invalidateQueries({ queryKey: inboxKey });

/**
 * A live notification the bell keeps goes straight in, without waiting for the server to have
 * saved it. read: it's already on screen (the chat it's from is open).
 */
export function addToInbox(qc: QueryClient, n: SocialNotification, read = false) {
  if (!kept.has(n.type)) return;
  qc.setQueryData<Inbox>(inboxKey, (d) => {
    if (!d || d.items.some((i) => i.id === n.id)) return d;
    const item = { ...n, readAt: read ? n.at : null } as InboxItem;
    return { items: [item, ...d.items], unread: d.unread + (read ? 0 : 1) };
  });
}

const change = (qc: QueryClient, ids: string[] | undefined, f: (d: Inbox) => Inbox) => {
  qc.setQueryData<Inbox>(inboxKey, (d) => d && f(d));
  return ids ? { ids } : {};
};

/** Marks these as read, or all of them. */
export async function markInboxRead(qc: QueryClient, ids?: string[]) {
  const at = new Date().toISOString();
  const body = change(qc, ids, (d) => {
    const items = d.items.map((i) =>
      !i.readAt && (!ids || ids.includes(i.id)) ? { ...i, readAt: at } : i,
    );
    return { items, unread: items.filter((i) => !i.readAt).length };
  });
  await api('/api/notifications/read', { body }).finally(() => refreshInbox(qc));
}

/** Takes these off the list, or all of them. */
export async function clearInbox(qc: QueryClient, ids?: string[]) {
  const body = change(qc, ids, (d) => {
    const items = ids ? d.items.filter((i) => !ids.includes(i.id)) : [];
    return { items, unread: items.filter((i) => !i.readAt).length };
  });
  await api('/api/notifications/clear', { body }).finally(() => refreshInbox(qc));
}
